import React from 'react';

function PortfolioManagement() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <img src="/new-assets/pexels-mart-production-7709127.jpg" alt="Portfolio Management" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black/80" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em]">Portfolio Management</h1>
            <p className="mt-4 text-white/90 max-w-2xl text-lg">Personalized investment strategies tailored to your goals and risk profile.</p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Our Approach</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">Expert portfolio managers construct diversified portfolios optimized for risk-adjusted returns across global markets.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-start mt-10">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Comprehensive Portfolio Construction</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our portfolio management service combines deep market insights with sophisticated quantitative analysis to build portfolios that align with your investment objectives, time horizon, and risk tolerance. We employ a disciplined, research-driven approach that adapts to changing market conditions while maintaining a long-term strategic focus.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Each portfolio is constructed with careful attention to asset allocation, diversification across geographies and sectors, tax efficiency, and liquidity needs. Our investment committee meets regularly to review portfolio positioning, assess market risks, and identify opportunities across asset classes.
              </p>
              <div className="bg-blue-50 border-l-4 border-[#0056d7] p-5 rounded-r-lg">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-[#0056d7]">Our Track Record:</strong> Over $5 billion in assets under management with a proven history of outperformance across market cycles. Average client relationship tenure exceeds 12 years.
                </p>
              </div>
            </div>
          <div className="relative w-full max-w-2xl mx-auto sm:mx-0">
            <img src="/new-assets/pexels-pavel-danilyuk-5520284.jpg" alt="Portfolio management" className="w-full h-auto object-cover rounded-2xl shadow-xl" />
              <div className="absolute -bottom-5 -right-5 w-40 h-40 bg-[#0056d7] opacity-10 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Investment Strategies */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-10">Investment Strategies</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {title: 'Growth-Oriented', desc: 'Equity-focused portfolios targeting long-term capital appreciation through diversified exposure to high-quality growth companies and emerging market opportunities.'},
              {title: 'Income & Stability', desc: 'Balanced portfolios emphasizing dividend income, fixed income securities, and capital preservation while maintaining moderate growth potential.'},
              {title: 'Custom Solutions', desc: 'Tailored strategies incorporating alternatives, ESG criteria, concentrated positions, tax optimization, and specialized mandates aligned with your unique requirements.'}
            ].map((strategy, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{strategy.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{strategy.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Management Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Our Process</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">A disciplined, six-step approach to portfolio construction and ongoing management.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {step: '01', title: 'Discovery & Analysis', desc: 'Comprehensive assessment of your financial situation, goals, risk tolerance, and investment constraints.'},
              {step: '02', title: 'Strategic Asset Allocation', desc: 'Development of target allocation across asset classes based on capital market assumptions and portfolio optimization.'},
              {step: '03', title: 'Security Selection', desc: 'Rigorous research process to identify best-in-class managers, funds, and individual securities for portfolio implementation.'},
              {step: '04', title: 'Portfolio Construction', desc: 'Thoughtful assembly of holdings with attention to diversification, correlation, tax efficiency, and fee minimization.'},
              {step: '05', title: 'Ongoing Monitoring', desc: 'Continuous surveillance of portfolio performance, risk metrics, and market conditions with quarterly formal reviews.'},
              {step: '06', title: 'Rebalancing & Optimization', desc: 'Systematic rebalancing to target allocations, tax-loss harvesting, and tactical adjustments as warranted by market conditions.'}
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4 p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#0056d7] flex items-center justify-center text-white font-bold text-sm">{item.step}</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Performance & Reporting */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold">Transparency & Reporting</h2>
            <p className="mt-3 text-white/80 max-w-3xl mx-auto">Comprehensive performance reporting and ongoing communication to keep you informed.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {label: 'Real-Time Access', desc: 'Online portal with 24/7 access to positions, performance, and account documents'},
              {label: 'Quarterly Reviews', desc: 'Detailed performance attribution, market commentary, and outlook'},
              {label: 'Tax Reporting', desc: 'Year-end tax documents, realized gains/losses, and cost basis tracking'},
              {label: 'Custom Analytics', desc: 'Risk metrics, concentration analysis, and scenario modeling'}
            ].map((item, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
                <h4 className="font-semibold mb-2">{item.label}</h4>
                <p className="text-sm text-white/80">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold text-gray-900">Start Building Your Portfolio</h3>
          <p className="mt-2 text-gray-600">Schedule a consultation with our portfolio management team to discuss your investment objectives.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold hover:shadow-lg transition-shadow">Get Started</a>
            <a href="/contact" className="px-6 py-3 rounded-md border-2 border-gray-300 text-gray-700 hover:border-gray-400 transition-colors">Contact Us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default PortfolioManagement;

